import { Box, Icon, Tag, Text } from '@factorialco/deprecated-design-system'
import { SettingPageCustomTimeRangeFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { categoryTimeTypeTag } from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/SpecialWorkHours/utils/categoryNameWithTimeTypeLabel'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import CustomTimeRangeUtils from 'modules/attendance/lib/utils/CustomTimeRangeUtils'

interface TagsContentProps {
  range: SettingPageCustomTimeRangeFragment
}

type SmallIcon = React.ComponentProps<typeof Icon.Small>['icon']

const getPoolTypeData = (
  range: SettingPageCustomTimeRangeFragment,
  hasBankOfHours: boolean
): { icon: SmallIcon; text: string } => {
  switch (range.poolType) {
    case 'virtual_timeoff':
      return {
        icon: 'palm-tree',
        text: i18n.t(
          'time_tracking.settings.work_time_categories.special_work_hours.cell.time_off'
        ),
      }
    case 'virtual_payroll':
      return {
        icon: 'dollar-bill',
        text: i18n.t('time_tracking.settings.work_time_categories.special_work_hours.cell.payroll'),
      }
    case 'balance':
    default:
      return {
        icon: 'balance',
        text: hasBankOfHours
          ? i18n.t(
              'time_tracking.settings.work_time_categories.special_work_hours.cell.bank_of_hours'
            )
          : i18n.t('time_tracking.settings.work_time_categories.special_work_hours.cell.balance'),
      }
  }
}

const TagsContent = ({ range }: TagsContentProps) => {
  const { name } = range
  const { hasBankOfHours, attendanceSetting } = useAttendanceSettingWorkCategories()
  const isFullDay = CustomTimeRangeUtils.rangeKey(range) === 'no_range'

  const timeRange = isFullDay
    ? i18n.t('time_tracking.settings.time_blocks.categories.modals.full_day')
    : CustomTimeRangeUtils.rangeKey(range)

  const poolTypeData = getPoolTypeData(range, hasBankOfHours)
  const hasSpecialHoursRegularHoursCompensation =
    attendanceSetting?.specialHoursRegularHoursCompensation

  const isRegular = CustomTimeRangeUtils.isRegular(range)

  const hidePoolType = !hasSpecialHoursRegularHoursCompensation && isRegular

  return (
    <Box gap="s8" alignItems="flexStart">
      {CustomTimeRangeUtils.isFlexible(range) ? (
        <Text.Normal size="200" weight="medium" ellipsis>
          {name}
        </Text.Normal>
      ) : (
        <Tag type={categoryTimeTypeTag(range.timeType)}>{name}</Tag>
      )}

      {range.date && typeof range.date === 'string' && (
        <Box flexDirection="row" alignItems="center" gap="s2">
          <Icon.Small icon="calendar" color="grey800" />
          <Text.Normal size="100" weight="normal">
            {range.date}
          </Text.Normal>
        </Box>
      )}

      <Box flexDirection="row" alignItems="center" gap="s2">
        <Icon.Small icon="clock" color="grey800" />
        <Text.Normal size="100" weight="normal">
          {timeRange}
        </Text.Normal>
      </Box>

      <Box flexDirection="row" alignItems="center" gap="s2">
        <Icon.Small icon="calculator" color="grey800" />
        <Text.Normal size="100" weight="normal">
          {CustomTimeRangeUtils.rawMinutes(range)}
        </Text.Normal>
        <Text.Normal size="100" weight="normal" color="grey800">
          {'->'}
        </Text.Normal>
        <Text.Normal size="100" weight="normal">
          {CustomTimeRangeUtils.equivalentMinutes(range)}
        </Text.Normal>
      </Box>
      {!hidePoolType && (
        <Box flexDirection="row" alignItems="center" gap="s2">
          <Icon.Small icon={poolTypeData.icon} color="grey800" />
          <Text.Normal size="100" weight="normal">
            {poolTypeData.text}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export default TagsContent
